import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { getSetupWizardEvents } from '../api/setupWizardApi';
import { DataTable } from '../components/DataTable';
import { ErrorState } from '../components/ErrorState';
import { LoadingState } from '../components/LoadingState';
import { PageShell } from '../components/PageShell';

export function SetupWizardEventsPage() {
  const { t } = useTranslation(['setupWizard', 'common']);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getSetupWizardEvents({ per_page: 100 });
      setRows(response.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo(() => [
    {
      id: 'tenant_id',
      label: t('setupWizard:events.columns.tenant'),
      render: (row) => (
        row.tenant_id ? (
          <RouterLink to={`/tenants/${row.tenant_id}`}>
            {t('setupWizard:dashboard.tenant_link', { id: row.tenant_id })}
          </RouterLink>
        ) : t('common:empty_value')
      ),
    },
    {
      id: 'event_type',
      label: t('setupWizard:events.columns.event'),
      render: (row) => t(`setupWizard:event_types.${row.event_type}`, row.event_type),
    },
    { id: 'step_code', label: t('setupWizard:events.columns.step') },
    { id: 'message', label: t('common:message') },
    {
      id: 'actor',
      label: t('setupWizard:events.columns.actor'),
      render: (row) => row.actor?.name || t('common:empty_value'),
    },
    { id: 'created_at', label: t('setupWizard:events.columns.at') },
  ], [t]);

  if (loading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState message={t('setupWizard:events.load_failed')} onRetry={load} />;
  }

  return (
    <PageShell title={t('setupWizard:events.title')} subtitle={t('setupWizard:events.subtitle')}>
      <DataTable columns={columns} rows={rows} rowKey="id" emptyMessage={t('setupWizard:events.no_events')} />
    </PageShell>
  );
}
